import { useEffect, useMemo, useState } from "react";
import { useDataSelectionContext } from "./DataSelectionContext";
import DataSelectionModel from "./DataSelectionModal";
import { WfsGetFeatureRequest } from "../../types/Wfs/WfsGetFeatureRequest";
import { Form } from "react-bootstrap";
import * as yup from "yup";
import useLayerService from "../../hooks/useLayerService";
import { RouteLineImport } from "../../types/RouteLine/RouteLineImport";
import LoadingSpinner from "../Loading/LoadingSpinner";

interface RouteLineFormValues {
    outputFormat: string;
    quarter?: string;
}

const RouteLines = () => {
    const layerName = "basemap:routeline";
    const { layers, getLayer, wfsOrder } = useDataSelectionContext();
    const layer = useMemo(() => getLayer(layerName), [layers]);
    const layerService = useLayerService();
    const [availableImports, setAvailableImports] = useState<RouteLineImport[] | null>(null);
    const [initialQuarter, setInitialQuarter] = useState<string | undefined>(undefined);
    const [loadingImports, setLoadingImports] = useState(false);

    useEffect(() => {
        setLoadingImports(true);
        
        layerService.GetRouteLineImportQuarters()
            .then((imports) => {
                const filtered = imports.filter(x => x.publishDate != null);
                setAvailableImports(filtered);
                
                if (filtered.length > 0) {
                    setInitialQuarter(filtered[filtered.length-1].importQuarter);
                }
            })
            .catch()
            .finally(() => {
                setLoadingImports(false);
            });
    }, []);

    const initialValues = useMemo(() =>
    ({
        outputFormat: "shapefile",
        quarter: initialQuarter
    } as RouteLineFormValues)
        , [layer, initialQuarter]);

    const onSubmit = (values: RouteLineFormValues) => {
        let filter = null;

        filter = "ImportQuarter = \"" + values.quarter + "\"";

        wfsOrder({
            service: "WFS",
            version: "2.0.0",
            request: "GetFeature",
            resultType: "Results",
            typeNames: layerName,
            outputFormat: values.outputFormat,
            filter: filter ?? undefined
        } as WfsGetFeatureRequest);
    };

    const schema = useMemo(
        () =>
            yup.object().shape({
                quarter: yup.string().required(),
                outputFormat: yup
                    .string()
                    .oneOf(["shapefile", "gml"])
                    .required(),
            }),
        [layer],
    );

    return <>
        <DataSelectionModel
            headerText="Basemap Route Lines"
            layer={layer}
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={onSubmit}
            enableReinitialize={true}>
            {({ values, handleChange, handleBlur }) =>
                    (<>
                        {!loadingImports && <>
                        <Form.Group>
                            <Form.Label htmlFor="outputFormat">Format:</Form.Label>
                            <Form.Select
                                id="outputFormat"
                                name="outputFormat"
                                value={values.outputFormat}
                                onChange={handleChange}
                                onBlur={handleBlur}>
                                <option value="gml">GML 3</option>
                                <option value="shapefile">ShapeFile</option>
                            </Form.Select>
                        </Form.Group>
                            <Form.Group className={"mt-3"}>
                                <Form.Label htmlFor="quarter">Import:</Form.Label>
                                <Form.Select
                                    id="quarter"
                                    name="quarter"
                                    value={values.quarter}
                                    onChange={handleChange}
                                    onBlur={handleBlur}>
                                    {availableImports && availableImports.map((x) =>
                                        <option value={x.importQuarter} key={x.id}>{x.importQuarter}</option>
                                    )}
                                </Form.Select>
                            </Form.Group></>}
                        {loadingImports && <>
                            <p>Loading data</p>
                            <LoadingSpinner />
                        </>}
                    </>)
            }
        </DataSelectionModel>
    </>;
}

export default RouteLines;